import { useState, useCallback } from 'react'
import { getSelectionDragMetrics } from '../utils/reorderSelection'

let activeRowDragState = {
  ghost: null,
  ghostClip: null,
  sourceOverlay: null,
  dragListener: null,
  dropListener: null,
  scrollListener: null,
  tableScroll: null
}

// Module-level ref shared between dragStart closure and drop handler
// so the native document drop listener can always read the latest values.
let pendingRowDrop = {
  fromIdx: null,
  toIdx: null,
  selectedRowIndexes: null,
  onReorder: null,
  tableContainer: null
}

const clearActiveRowDrag = () => {
  if (activeRowDragState.ghostClip && activeRowDragState.ghostClip.parentNode) {
    activeRowDragState.ghostClip.parentNode.removeChild(
      activeRowDragState.ghostClip
    )
  }
  if (activeRowDragState.ghost && activeRowDragState.ghost.parentNode) {
    activeRowDragState.ghost.parentNode.removeChild(activeRowDragState.ghost)
  }
  if (
    activeRowDragState.sourceOverlay &&
    activeRowDragState.sourceOverlay.parentNode
  ) {
    activeRowDragState.sourceOverlay.parentNode.removeChild(
      activeRowDragState.sourceOverlay
    )
  }
  if (activeRowDragState.dragListener) {
    document.removeEventListener('dragover', activeRowDragState.dragListener)
  }
  if (activeRowDragState.dropListener) {
    document.removeEventListener('drop', activeRowDragState.dropListener)
  }
  if (activeRowDragState.scrollListener && activeRowDragState.tableScroll) {
    activeRowDragState.tableScroll.removeEventListener(
      'scroll',
      activeRowDragState.scrollListener
    )
  }
  activeRowDragState = {
    ghost: null,
    ghostClip: null,
    sourceOverlay: null,
    dragListener: null,
    dropListener: null,
    scrollListener: null,
    tableScroll: null
  }
  pendingRowDrop = {
    fromIdx: null,
    toIdx: null,
    selectedRowIndexes: null,
    onReorder: null,
    tableContainer: null
  }
}

export function useRowReorder(
  selectedRowIndexes,
  onReorder,
  setSelectedRowIndexes
) {
  const [draggedRowIdx, setDraggedRowIdx] = useState(null)
  const [draggedRowKey, setDraggedRowKey] = useState(null)
  const [dropTargetRowIdx, setDropTargetRowIdx] = useState(null)
  const [, setIsAtBottom] = useState(false)

  const refreshSourceOverlayPosition = useCallback(() => {
    if (!activeRowDragState.sourceOverlay || !draggedRowKey) return

    const tableContainer = activeRowDragState.tableScroll?.closest
      ? activeRowDragState.tableScroll.closest('.rp-table-container')
      : null

    // Find current row DOM by stable rowKey
    const tableScroll = activeRowDragState.tableScroll
    if (!tableScroll) return

    // Safer: scan table rows for exact key
    const tc = tableContainer || document

    const rows = tc.querySelectorAll(
      'tr[data-row-hinmei-code][data-row-niukabango]'
    )

    let targetTr = null
    for (const r of rows) {
      const hinmei = r.getAttribute('data-row-hinmei-code')
      const niu = r.getAttribute('data-row-niukabango')
      if (hinmei && niu && `${hinmei}::${niu}` === draggedRowKey) {
        targetTr = r
        break
      }
    }
    if (!targetTr) return

    const trRect = targetTr.getBoundingClientRect()
    const scrollRect = tableScroll.getBoundingClientRect()
    const tableRect = targetTr.closest('table')
      ? targetTr.closest('table').getBoundingClientRect()
      : scrollRect

    // Keep overlay inside scroll content coordinates (same scheme used at dragStart)
    activeRowDragState.sourceOverlay.style.top = `${
      trRect.top - scrollRect.top + tableScroll.scrollTop
    }px`

    // Note: original left used tableRect + scrollLeft delta.
    const fixedLeft = tableRect.left - scrollRect.left + tableScroll.scrollLeft
    activeRowDragState.sourceOverlay.style.left = `${fixedLeft}px`
  }, [draggedRowKey])

  const handleRowDragStart = useCallback(
    (e, rowIdx) => {
      // Ensure any previous stuck ghost is cleared
      clearActiveRowDrag()

      const nextSelection = selectedRowIndexes.includes(rowIdx)
        ? selectedRowIndexes
        : [rowIdx]

      if (!selectedRowIndexes.includes(rowIdx) && setSelectedRowIndexes) {
        setSelectedRowIndexes(nextSelection)
      }

      setDraggedRowIdx(rowIdx)

      // Stable row identity (rowId/rowKey) so UI source highlight follows reordering.
      const trKey = (() => {
        const rowEl = e.currentTarget.closest('tr')
        if (!rowEl) return null
        const hinmei = rowEl.getAttribute('data-row-hinmei-code')
        const niu = rowEl.getAttribute('data-row-niukabango')
        return hinmei && niu ? `${hinmei}::${niu}` : null
      })()
      setDraggedRowKey(trKey)

      e.dataTransfer.effectAllowed = 'move'

      // Seed the module-level ref so the native drop listener knows what's being dragged
      pendingRowDrop.fromIdx = rowIdx
      pendingRowDrop.selectedRowIndexes = nextSelection
      pendingRowDrop.onReorder = onReorder

      // Transparent 1x1 image native drag preview
      const dragImage = document.createElement('canvas')
      dragImage.width = 1
      dragImage.height = 1
      e.dataTransfer.setDragImage(dragImage, 0, 0)

      const tr = e.currentTarget.closest('tr')
      if (!tr) return

      const trRect = tr.getBoundingClientRect()
      const tableScroll = tr.closest('.rp-table-scroll')
      const tableContainer = tr.closest('.rp-table-container')
      const scrollRect = tableScroll.getBoundingClientRect()
      const table = tr.closest('table')
      const tableRect = table ? table.getBoundingClientRect() : scrollRect
      const tbodyElement = table?.querySelector('tbody')
      const rowElementsByIndex = new Map(
        Array.from(
          tbodyElement?.querySelectorAll('tr[data-row-idx]') || []
        ).map((rowElement) => [
          Number(rowElement.getAttribute('data-row-idx')),
          rowElement
        ])
      )
      const selectedRects = nextSelection
        .map((idx) => rowElementsByIndex.get(idx)?.getBoundingClientRect())
        .filter(Boolean)
      const ghostSize = getSelectionDragMetrics({
        selectedRects,
        fallbackRect: { width: tableRect.width, height: trRect.height },
        direction: 'vertical'
      })

      // Keep module-level ref updated so the native drop listener has full context
      pendingRowDrop.tableContainer = tableContainer

      const ghost = document.createElement('div')
      ghost.id = 'custom-row-drag-ghost'
      ghost.style.position = 'absolute'
      ghost.style.width = `${ghostSize.width}px`
      ghost.style.height = `${ghostSize.height}px`
      ghost.style.backgroundColor = 'rgba(0, 0, 0, 0.2)'
      ghost.style.border = '1px solid #d1d5db'
      ghost.style.pointerEvents = 'none'
      ghost.style.zIndex = '10000'
      ghost.style.transition = 'none'
      ghost.style.boxSizing = 'border-box'
      ghost.style.top = `0`
      ghost.style.willChange = 'transform'

      // No inner HTML for row ghost
      ghost.innerHTML = ''

      // Render the ghost inside a viewport-sized clip layer (Cách C).
      // The ghost is free to follow the cursor all the way to the last row;
      // whatever spills past the visible area is clipped instead of expanding
      // the scroll height, so it never gets "stuck" before the real end.
      const containerRect = tableContainer.getBoundingClientRect()
      if (!tableContainer.style.position) {
        tableContainer.style.position = 'relative'
      }

      const ghostClip = document.createElement('div')
      ghostClip.id = 'custom-row-drag-clip'
      ghostClip.style.position = 'absolute'
      ghostClip.style.left = `${scrollRect.left - containerRect.left}px`
      ghostClip.style.top = `${scrollRect.top - containerRect.top}px`
      ghostClip.style.width = `${tableScroll.clientWidth}px`
      ghostClip.style.height = `${tableScroll.clientHeight}px`
      ghostClip.style.overflow = 'hidden'
      ghostClip.style.pointerEvents = 'none'
      ghostClip.style.zIndex = '10000'
      ghostClip.appendChild(ghost)
      tableContainer.appendChild(ghostClip)

      // The ghost is positioned in VIEWPORT coordinates relative to the clip
      // layer (which overlays the scroll viewport). No scrollTop is added, so
      // it stays under the cursor while the content scrolls beneath it.
      // At drag start the ghost must sit exactly on top of the source
      // selection, so anchor to the first selected row (not the grabbed row).
      const firstSelectedRect = selectedRects[0] || trRect
      ghost.style.left = `${tableRect.left - scrollRect.left}px`
      ghost.style.top = '0'

      // Keep the ghost aligned with the source selection's top edge while it
      // follows the cursor vertically.
      const offsetY = e.clientY - firstSelectedRect.top

      let lastClientY = e.clientY

      // Top boundary so the ghost never covers the sticky header rows
      const getHeaderBottom = () => {
        const theadElement = table.querySelector('thead')
        if (!theadElement) return 0
        const headerRows = theadElement.querySelectorAll('tr')
        if (headerRows.length === 0) return 0
        const lastHeaderRow = headerRows[headerRows.length - 1]
        return lastHeaderRow.getBoundingClientRect().bottom - scrollRect.top
      }

      const updatePosition = (clientY) => {
        lastClientY = clientY

        // Viewport-relative Y (no scrollTop): the ghost tracks the cursor and
        // the clip layer hides whatever spills past the bottom edge.
        let y = clientY - scrollRect.top - offsetY

        const currentMinY = getHeaderBottom()

        // Gentle auto-scroll up when hovering near the sticky header
        if (clientY < scrollRect.top + 200) {
          tableScroll.scrollTop -= 10
        }

        // Only clamp the top edge; the bottom is intentionally free and clipped
        // by the wrapper so the scroll container never grows.
        if (y < currentMinY) y = currentMinY

        ghost.style.transform = `translateY(${y}px)`
      }

      updatePosition(e.clientY)

      const handleDragOverDoc = (dragEvent) => {
        if (dragEvent.clientX === 0 && dragEvent.clientY === 0) return
        updatePosition(dragEvent.clientY)
      }

      const handleScroll = () => {
        updatePosition(lastClientY)
      }

      document.addEventListener('dragover', handleDragOverDoc)
      tableScroll.addEventListener('scroll', handleScroll)

      // Create source overlay on the dragged row. This one lives inside the
      // scroll content (not the clip layer) so it stays anchored to the
      // original row position and scrolls with the table.
      tableScroll.style.position = 'relative'
      const sourceOverlay = document.createElement('div')
      sourceOverlay.className = 'drag-source-overlay'
      sourceOverlay.style.position = 'absolute'
      sourceOverlay.style.width = `${ghostSize.width}px`
      sourceOverlay.style.height = `${ghostSize.height}px`
      sourceOverlay.style.top = `${firstSelectedRect.top - scrollRect.top + tableScroll.scrollTop}px`
      sourceOverlay.style.left = `${tableRect.left - scrollRect.left + tableScroll.scrollLeft}px`
      sourceOverlay.style.pointerEvents = 'none'
      sourceOverlay.style.zIndex = '9999'
      tableScroll.appendChild(sourceOverlay)

      activeRowDragState = {
        ghost,
        ghostClip,
        sourceOverlay,
        dragListener: handleDragOverDoc,
        dropListener: null, // will be set below
        scrollListener: handleScroll,
        tableScroll
      }

      // Native document-level drop listener — catches drops when cursor is outside
      // any React-managed element (e.g. above thead or below the scroll container).
      // Reads from pendingRowDrop to avoid stale closure issues.
      const handleDropDoc = (dropEvent) => {
        dropEvent.preventDefault()

        const {
          fromIdx,
          toIdx,
          selectedRowIndexes: selectedIndexes,
          onReorder: reorder,
          tableContainer: tc
        } = pendingRowDrop

        // Clear state first to prevent double-fire with React onDrop handlers
        pendingRowDrop = {
          fromIdx: null,
          toIdx: null,
          selectedRowIndexes: null,
          onReorder: null,
          tableContainer: null
        }

        setDraggedRowIdx(null)
        setDraggedRowKey(null)
        setDropTargetRowIdx(null)
        setIsAtBottom(false)

        if (tc) tc.classList.remove('is-dragging')

        // Ensure source overlay follows the dragged item after reorder.
        // (Reconnect overlay position to current DOM by rowKey.)
        if (
          reorder &&
          fromIdx !== null &&
          toIdx !== null &&
          fromIdx !== toIdx
        ) {
          // reorder(...) will run below; after it completes DOM positions are updated.
          // We rely on document querying immediately after reorder.
        }

        clearActiveRowDrag()

        if (fromIdx === null || toIdx === null || fromIdx === toIdx) return
        if (reorder) reorder(fromIdx, toIdx, selectedIndexes)
      }

      activeRowDragState.dropListener = handleDropDoc
      document.addEventListener('drop', handleDropDoc)

      if (tableContainer) {
        tableContainer.classList.add('is-dragging')
      }
    },
    [onReorder, selectedRowIndexes, setSelectedRowIndexes]
  )

  const handleRowDragEnd = useCallback((e) => {
    setDraggedRowIdx(null)
    setDraggedRowKey(null)
    setDropTargetRowIdx(null)
    setIsAtBottom(false)

    clearActiveRowDrag()

    const tableContainer = e.currentTarget.closest('.rp-table-container')
    if (tableContainer) {
      tableContainer.classList.remove('is-dragging')
    }
  }, [])

  const handleRowDragOver = useCallback(
    (e, _passedRowIdx) => {
      if (draggedRowIdx === null) return

      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'

      // Auto-scroll vertically
      const scrollContainer = e.currentTarget.closest('.rp-table-scroll')
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()

        let stickyHeight = 0
        const table = scrollContainer.querySelector('.rp-table')
        if (table) {
          const thead = table.querySelector('thead')
          if (thead) {
            const theadRect = thead.getBoundingClientRect()
            stickyHeight = Math.max(0, theadRect.bottom - containerRect.top)
          }
        }

        const topThreshold = 40
        const bottomThreshold = 60

        if (e.clientY < containerRect.top + stickyHeight + topThreshold) {
          const intensity = Math.max(
            1,
            (containerRect.top + stickyHeight + topThreshold - e.clientY) / 5
          )
          scrollContainer.scrollTop -= 5 * intensity
        } else if (e.clientY > containerRect.bottom - bottomThreshold) {
          const intensity = Math.max(
            1,
            (e.clientY - (containerRect.bottom - bottomThreshold)) / 5
          )
          scrollContainer.scrollTop += 5 * intensity
        }
      }

      // Determine target row purely by vertical mouse position (Y coordinate)
      const table = e.currentTarget.classList.contains('rp-table-scroll')
        ? e.currentTarget.querySelector('.rp-table')
        : e.currentTarget.closest('.rp-table')

      if (!table) return

      const tbody = table.querySelector('tbody')
      if (!tbody) return

      const trElements = Array.from(tbody.querySelectorAll('tr[data-row-idx]'))
      let targetIdx = null
      let targetRect = null

      for (const tr of trElements) {
        const rect = tr.getBoundingClientRect()
        // e.clientY within the vertical bounds of the row
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          targetIdx = parseInt(tr.getAttribute('data-row-idx'), 10)
          targetRect = rect
          break
        }
      }

      // Apply a drop target while suppressing it when the drop would land
      // inside the source selection or on either of its edges (a no-op drop).
      const applyDropTarget = (toIdx, atBottom) => {
        const selectedIndexes = (
          pendingRowDrop.selectedRowIndexes || [draggedRowIdx]
        ).filter((idx) => typeof idx === 'number')

        if (selectedIndexes.length > 0) {
          const minSelection = Math.min(...selectedIndexes)
          const maxSelection = Math.max(...selectedIndexes)
          if (toIdx >= minSelection && toIdx <= maxSelection + 1) {
            setDropTargetRowIdx(null)
            setIsAtBottom(false)
            pendingRowDrop.toIdx = null
            return
          }
        }

        setDropTargetRowIdx(toIdx)
        setIsAtBottom(atBottom)
        pendingRowDrop.toIdx = toIdx
      }

      // Fallback: cursor is outside the row list (above first row or below last row)
      if (targetIdx === null || !targetRect) {
        if (trElements.length === 0) return

        const firstTr = trElements[0]
        const lastTr = trElements[trElements.length - 1]
        const firstRect = firstTr.getBoundingClientRect()
        const lastRect = lastTr.getBoundingClientRect()

        if (e.clientY < firstRect.top) {
          // Cursor is above the first row → drop at the very beginning
          const idx = parseInt(firstTr.getAttribute('data-row-idx'), 10)
          applyDropTarget(idx, false)
        } else if (e.clientY > lastRect.bottom) {
          // Cursor is below the last row → drop at the very end
          const lastIdx = parseInt(lastTr.getAttribute('data-row-idx'), 10)
          applyDropTarget(lastIdx + 1, true)
        }
        return
      }

      const y = e.clientY - targetRect.top
      const isBottomHalf = y > targetRect.height / 2

      if (isBottomHalf) {
        applyDropTarget(targetIdx + 1, true)
      } else {
        applyDropTarget(targetIdx, false)
      }
    },
    [draggedRowIdx]
  )

  const handleRowDrop = useCallback(
    (e, _passedTargetIdx) => {
      e.preventDefault()
      e.stopPropagation()

      // If native document drop listener already handled this drop (cursor was outside
      // any React element), pendingRowDrop.fromIdx has been cleared — skip to avoid
      // double-firing the reorder.
      if (pendingRowDrop.fromIdx === null && draggedRowIdx === null) return

      const fromIdx = draggedRowIdx
      const toIdx = dropTargetRowIdx
      const selectedIndexes = pendingRowDrop.selectedRowIndexes || [
        draggedRowIdx
      ]

      // Clear module-level ref so native listener won't double-fire
      pendingRowDrop = {
        fromIdx: null,
        toIdx: null,
        selectedRowIndexes: null,
        onReorder: null,
        tableContainer: null
      }

      setDraggedRowIdx(null)
      setDropTargetRowIdx(null)
      setIsAtBottom(false)

      clearActiveRowDrag()

      const tableContainer = e.currentTarget.closest('.rp-table-container')
      if (tableContainer) {
        tableContainer.classList.remove('is-dragging')
      }

      if (fromIdx === null || toIdx === null) return

      const validIndexes = selectedIndexes.filter((idx) => typeof idx === 'number')
      if (validIndexes.length > 0) {
        const minSelection = Math.min(...validIndexes)
        const maxSelection = Math.max(...validIndexes)
        if (toIdx >= minSelection && toIdx <= maxSelection + 1) return
      } else if (fromIdx === toIdx) {
        return
      }

      if (onReorder) {
        onReorder(fromIdx, toIdx, selectedIndexes)
      }

      if (setSelectedRowIndexes) {
        const normalizedSelection = Array.from(
          new Set(
            (selectedIndexes || [])
              .map((index) => Number(index))
              .filter((index) => Number.isInteger(index) && index >= 0)
              .sort((a, b) => a - b)
          )
        )
        const normalizedFrom = Number(fromIdx)
        const normalizedTo = Number(toIdx)
        const adjustedToIndex =
          normalizedFrom < normalizedTo
            ? Math.max(normalizedTo - normalizedSelection.length, 0)
            : normalizedTo

        const newSelectedIndexes = normalizedSelection.map(
          (_, i) => adjustedToIndex + i
        )
        setSelectedRowIndexes(newSelectedIndexes)
      }

      // After reorder, ensure the drag source overlay follows the moved row.
      // Use microtask so DOM updates from state reorder are applied.
      Promise.resolve().then(() => {
        refreshSourceOverlayPosition()
      })
    },
    [draggedRowIdx, dropTargetRowIdx, onReorder, refreshSourceOverlayPosition]
  )

  return {
    handleRowDragStart,
    handleRowDragEnd,
    handleRowDragOver,
    handleRowDrop,
    draggedRowIdx,
    draggedRowKey,
    dropTargetRowIdx
  }
}
